export type MessageRewindTarget =
  | { kind: "transcript"; transcriptId: string }
  | { kind: "index"; messageIndex: number };

export function readTranscriptId(message: unknown): string | null {
  if (!message || typeof message !== "object") {
    return null;
  }
  const record = message as Record<string, unknown>;
  const id = record.transcriptId ?? record.id;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

export function isUserMessage(message: unknown): boolean {
  if (!message || typeof message !== "object") {
    return false;
  }
  const role = (message as Record<string, unknown>).role;
  return typeof role === "string" && role.toLowerCase() === "user";
}

export function isAssistantMessage(message: unknown): boolean {
  if (!message || typeof message !== "object") {
    return false;
  }
  const role = (message as Record<string, unknown>).role;
  return typeof role === "string" && role.toLowerCase() === "assistant";
}

/** Resolve gateway rewind params for a transcript message (id preferred, index fallback). */
export function resolveMessageRewindTarget(
  messages: unknown[],
  message: unknown,
): MessageRewindTarget | null {
  const transcriptId = readTranscriptId(message);
  if (transcriptId) {
    return { kind: "transcript", transcriptId };
  }
  if (!message || typeof message !== "object") {
    return null;
  }
  for (let i = 0; i < messages.length; i++) {
    if (messages[i] === message) {
      return { kind: "index", messageIndex: i };
    }
  }
  return null;
}

export function rewindTargetToRewindParams(
  target: MessageRewindTarget,
): { beforeTranscriptId: string } | { beforeMessageIndex: number } {
  return target.kind === "transcript"
    ? { beforeTranscriptId: target.transcriptId }
    : { beforeMessageIndex: target.messageIndex };
}

export function rewindTargetsEqual(a: MessageRewindTarget, b: MessageRewindTarget): boolean {
  if (a.kind !== b.kind) {
    return false;
  }
  if (a.kind === "transcript" && b.kind === "transcript") {
    return a.transcriptId === b.transcriptId;
  }
  if (a.kind === "index" && b.kind === "index") {
    return a.messageIndex === b.messageIndex;
  }
  return false;
}
